import { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useCoach } from './CoachProvider.js';
import { useAnchor } from './useAnchor.js';
import type { Rect } from './useAnchor.js';
import { chipLabel, chipValue } from './types.js';
import type { Input, Media, Step } from './types.js';

/**
 * The one thing on screen. Render it once, near the root.
 *
 * Every colour is a custom property with a fallback chain, so the package
 * themes itself from the host's tokens when they exist and still looks
 * deliberate when lifted into an app that has none.
 */
export type CoachLabels = {
  next: string;
  done: string;
  skip: string;
  /** Shown on an input step that may be passed over. */
  notNow: string;
};

const PAD = 8;
const BUBBLE_W = 300;

/** Assumed height until the real one is measured. Only ever used for one
 *  frame, and in jsdom, which has no layout. */
const BUBBLE_H = 160;

export function Coach({ labels }: { labels: CoachLabels }) {
  const coach = useCoach();
  const current = coach.current;
  const step = current?.step ?? null;
  const anchor = step && step.kind !== 'modal' ? step.anchor : null;
  const found = useAnchor(anchor, step?.waitMs);

  // An anchor that never arrived retires the step. Done in an effect rather
  // than during render because it changes provider state.
  useEffect(() => {
    if (anchor !== null && found.status === 'missing') coach.skip();
  }, [anchor, found.status, coach]);

  if (!current || !step) return null;
  if (step.kind === 'modal') return <ModalStep step={step} labels={labels} />;
  if (found.status !== 'found') return null;

  return (
    <>
      <PulseStyle />
      {step.kind === 'beacon' ? (
        <BeaconStep rect={found.rect} body={step.body} />
      ) : (
        <SpotlightStep rect={found.rect} step={step} labels={labels} />
      )}
    </>
  );
}

function SpotlightStep({
  rect,
  step,
  labels,
}: {
  rect: Rect;
  step: Extract<Step, { kind: 'spotlight' }>;
  labels: CoachLabels;
}) {
  const coach = useCoach();
  const card = useRef<HTMLDivElement>(null);
  const height = useBubbleHeight(card, step);

  // The bubble goes below the target when there is room and above when there
  // is not — but on a phone a spotlit CONTAINER (a composer, a wrapping row
  // of chips that has gone to two lines) can leave room on neither side, and
  // the old arithmetic then put the bubble half off the bottom of the screen
  // where nobody could read it or reach Skip. When neither side fits, the
  // bubble is pinned to the roomier edge of the VIEWPORT and allowed to
  // overlap the target: covering part of what is being explained is bad, and
  // being unreachable is worse.
  const gapBelow = window.innerHeight - (rect.top + rect.height) - PAD * 3;
  const gapAbove = rect.top - PAD * 3;

  const top =
    gapBelow >= height
      ? rect.top + rect.height + PAD * 2
      : gapAbove >= height
        ? rect.top - PAD * 2 - height
        : gapBelow >= gapAbove
          ? Math.max(PAD, window.innerHeight - height - PAD)
          : PAD;

  // Never wider than the screen. `BUBBLE_W` was a fixed 300, which overflows
  // a 320px phone once the padding is counted.
  const width = Math.min(BUBBLE_W, window.innerWidth - PAD * 2);
  const left = Math.min(
    Math.max(PAD, rect.left + rect.width / 2 - width / 2),
    Math.max(PAD, window.innerWidth - width - PAD),
  );

  return (
    <>
      <Scrim rect={rect} onDismiss={coach.dismiss} />
      <div
        ref={card}
        role="dialog"
        aria-live="polite"
        style={{ ...bubble, top, left, width }}
      >
        {step.title !== undefined && <strong style={titleStyle}>{step.title}</strong>}
        <p style={bodyStyle}>{step.body}</p>
        <div style={row}>
          <button type="button" onClick={coach.dismiss} style={ghostButton}>
            {labels.skip}
          </button>
          {/* A step that asks for an action gets NO next button. Offering one
              lets the student agree without doing the thing, and then the
              flow has taught nothing. */}
          {(step.advance === undefined || step.advance === 'next') && (
            <button type="button" onClick={coach.next} style={primaryButton}>
              {labels.next}
            </button>
          )}
        </div>
      </div>
    </>
  );
}

/**
 * How tall the bubble actually is.
 *
 * Guessing was the old approach — a hardcoded 150 — and it is wrong by a lot
 * in Bangla, where the same sentence runs a line or two longer, which is
 * exactly the case where a mispositioned bubble goes off the screen. Measured
 * after layout, re-measured when the copy changes, with the guess kept only
 * for the very first frame and for jsdom, which has no layout at all.
 */
function useBubbleHeight(ref: React.RefObject<HTMLDivElement | null>, step: Step): number {
  const [height, setHeight] = useState(BUBBLE_H);

  useLayoutEffect(() => {
    const measured = ref.current?.getBoundingClientRect().height ?? 0;
    if (measured > 0) setHeight(measured);
  }, [ref, step]);

  return height;
}

/** The quietest of the three: a pulse that interrupts nothing and waits. */
function BeaconStep({ rect, body }: { rect: Rect; body: string }) {
  const coach = useCoach();
  return (
    <button
      type="button"
      aria-label={body}
      onClick={coach.next}
      style={{
        position: 'fixed',
        top: rect.top + rect.height / 2 - 8,
        left: rect.left + rect.width / 2 - 8,
        width: 16,
        height: 16,
        borderRadius: 999,
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        background: 'var(--coach-ring, var(--aj-accent, #C2410C))',
        boxShadow: '0 0 0 6px var(--coach-halo, rgba(194, 65, 12, 0.25))',
      }}
    />
  );
}

function ModalStep({
  step,
  labels,
}: {
  step: Extract<Step, { kind: 'modal' }>;
  labels: CoachLabels;
}) {
  const coach = useCoach();
  const [text, setText] = useState('');
  const input = step.input;
  const last = step.advance === undefined || step.advance === 'next';

  return (
    <div style={backdrop} role="dialog" aria-modal="true">
      <div style={{ ...bubble, position: 'relative', width: 'min(420px, calc(100vw - 32px))' }}>
        {step.media && <MediaBlock media={step.media} />}
        {step.title !== undefined && <strong style={titleStyle}>{step.title}</strong>}
        <p style={bodyStyle}>{step.body}</p>

        {input && <InputBlock input={input} text={text} onText={setText} onPick={coach.answer} />}

        <div style={row}>
          <button type="button" onClick={coach.dismiss} style={ghostButton}>
            {input?.optional ? labels.notNow : labels.skip}
          </button>
          {input
            ? input.allowFreeText === true && (
                <button
                  type="button"
                  disabled={text.trim().length === 0}
                  onClick={() => coach.answer(text.trim())}
                  style={{ ...primaryButton, opacity: text.trim() ? 1 : 0.5 }}
                >
                  {labels.done}
                </button>
              )
            : last && (
                <button type="button" onClick={coach.next} style={primaryButton}>
                  {labels.next}
                </button>
              )}
        </div>
      </div>
    </div>
  );
}

/**
 * Layers.
 *
 * The app's own stacking contexts run to `z-50` — a sticky nav, sticky day
 * headers, a sticky lead column in the wall chart. The scrim MUST sit above
 * all of them: without this it paints underneath, and the nav bar and every
 * sticky header stay bright on top of a dimmed page, which looks like a
 * rendering fault rather than a spotlight.
 */
const SCRIM_Z = 9_998;
const RING_Z = 9_999;
const BUBBLE_Z = 10_000;

/**
 * The cutout, as four rectangles around the target rather than one overlay
 * with a hole.
 *
 * The four-panel version is what makes the highlighted control genuinely
 * usable: there is no element over it, so the host's own button receives the
 * tap and does its own work. A single overlay with a visual hole would still
 * swallow the click, and the whole point of coaching on the live UI is that
 * the student presses the real thing.
 */
function Scrim({ rect, onDismiss }: { rect: Rect; onDismiss: () => void }) {
  const scrim = 'var(--coach-scrim, rgba(15, 18, 22, 0.62))';
  // Clamped to the viewport so a target scrolled half off the top cannot make
  // a panel start at a negative offset and cover the thing it is exposing.
  const top = Math.max(0, rect.top - PAD);
  const bottom = Math.max(0, rect.top + rect.height + PAD);
  const left = Math.max(0, rect.left - PAD);
  const right = Math.max(0, rect.left + rect.width + PAD);

  const panel = (style: React.CSSProperties, key: string) => (
    <div
      key={key}
      aria-hidden
      onClick={onDismiss}
      style={{ position: 'fixed', background: scrim, zIndex: SCRIM_Z, ...style }}
    />
  );

  return (
    <>
      {panel({ top: 0, left: 0, right: 0, height: top }, 'top')}
      {panel({ top: bottom, left: 0, right: 0, bottom: 0 }, 'bottom')}
      {panel({ top, left: 0, width: left, height: Math.max(0, bottom - top) }, 'left')}
      {panel({ top, left: right, right: 0, height: Math.max(0, bottom - top) }, 'right')}

      {/* The highlight: one steady outline with a soft halo, and nothing that
          moves. Scaling this outline was the earlier attempt, and on a wide
          button it grew far more sideways than up — the "horizontal throb"
          that made it read as a layout glitch rather than a pointer. A shape
          that marks a target should hold still; the motion belongs in one
          small thing. */}
      <div
        aria-hidden
        style={{
          position: 'fixed',
          top: rect.top - PAD,
          left: rect.left - PAD,
          width: rect.width + PAD * 2,
          height: rect.height + PAD * 2,
          border: '2px solid var(--coach-ring, var(--aj-accent, #C2410C))',
          boxShadow: '0 0 0 5px var(--coach-halo, rgba(194, 65, 12, 0.22))',
          borderRadius: 12,
          pointerEvents: 'none',
          zIndex: RING_Z,
        }}
      />

      <PulseDot rect={rect} />
    </>
  );
}

/** The dot, and the widest its rings ever get (a multiple of the dot). */
const DOT = 14;

/** At or below this width a target is small enough — an icon button, a
 *  checkbox — that its middle is empty and the dot belongs there. Anything
 *  wider is carrying a label, so the dot sits inside the trailing edge where
 *  a thumb is going anyway. */
const SMALL = 72;

/** Inset from that trailing edge. Far enough in to read as ON the control. */
const EDGE_INSET = 22;

/**
 * "Press here", as one small pulsing circle.
 *
 * Two rings ping out of a solid dot, offset by half a cycle so the motion is
 * continuous rather than a blink with a gap. It is deliberately the ONLY
 * moving thing on screen: the ring around the target holds still, and one
 * point of motion is what the eye actually follows.
 *
 * Inert, so it never intercepts the very tap it is advertising.
 */
function PulseDot({ rect }: { rect: Rect }) {
  const centred = rect.width <= SMALL;
  const cx = centred
    ? rect.left + rect.width / 2
    : rect.left + rect.width - Math.min(EDGE_INSET, rect.width / 2);
  const cy = rect.top + rect.height / 2;

  // The rings take the dot's colour, not the accent: they are the same
  // object spreading outwards, and two hues here would read as two things
  // overlapping rather than one pulse.
  const ping: React.CSSProperties = {
    position: 'absolute',
    inset: 0,
    borderRadius: 999,
    background: 'var(--coach-dot, #4b5563)',
  };

  return (
    <div
      aria-hidden
      style={{
        position: 'fixed',
        top: cy - DOT / 2,
        left: cx - DOT / 2,
        width: DOT,
        height: DOT,
        pointerEvents: 'none',
        zIndex: RING_Z,
      }}
    >
      <div className="aj-coach-ping" style={ping} />
      <div className="aj-coach-ping" style={{ ...ping, animationDelay: '0.55s' }} />
      {/* Solid, not outlined. A ring with a hollow middle reads as a marker —
          something pointing at the thing. A filled disc reads as a button:
          the place a finger goes. The white halo sits OUTSIDE it so the disc
          keeps its full weight and still separates from a dark control.

          Grey, not accent. The accent is the app's own colour for things that
          are ACTIONABLE, and this disc sits on top of a real control that is
          already wearing it — two oranges an inch apart is where the pointer
          stops being distinguishable from what it is pointing at. Neutral
          against the accent ring is the contrast that does the work. */}
      <div
        className="aj-coach-dot"
        style={{
          ...ping,
          background: 'var(--coach-dot, #4b5563)',
          boxShadow: '0 0 0 2px var(--coach-on-accent, #fff), 0 1px 4px rgba(0,0,0,0.35)',
        }}
      />
    </div>
  );
}

/**
 * Keyframes for the pulse, injected once.
 *
 * The package ships no stylesheet — it is meant to lift out as a directory —
 * so the one animation it needs travels with it. `prefers-reduced-motion`
 * turns it off entirely rather than slowing it down: a repeating pulse is
 * exactly the kind of perpetual motion that rule exists for.
 */
function PulseStyle() {
  return (
    <style>{`
      @keyframes aj-coach-ping {
        0%   { transform: scale(1);   opacity: 0.65; }
        100% { transform: scale(3.4); opacity: 0;    }
      }
      @keyframes aj-coach-dot {
        0%, 100% { transform: scale(1);   }
        50%      { transform: scale(1.3); }
      }
      /* Roughly a resting heartbeat. Slower reads as ambient decoration —
         something the screen is doing — and this has to read as an
         instruction with a target. */
      .aj-coach-ping { animation: aj-coach-ping 1.1s ease-out infinite; }
      .aj-coach-dot  { animation: aj-coach-dot 1.1s ease-in-out infinite; }
      @media (prefers-reduced-motion: reduce) {
        /* The rings go entirely; the dot stays, because it is what says
           WHERE inside a wide control the press belongs. */
        .aj-coach-ping { display: none; }
        .aj-coach-dot  { animation: none; }
      }
    `}</style>
  );
}

/**
 * Media is lazy and never preloaded.
 *
 * A tutorial clip that has to download before the app is usable is worse than
 * no clip, and this app is offline-first on metered connections. `preload`
 * stays `none` so a step with a video costs nothing until it is reached.
 */
function MediaBlock({ media }: { media: Media }) {
  const frame: React.CSSProperties = {
    width: '100%',
    borderRadius: 10,
    marginBottom: 12,
    display: 'block',
    background: 'var(--coach-sunken, rgba(0,0,0,0.06))',
  };

  return media.kind === 'video' ? (
    <video
      style={frame}
      src={media.src}
      {...(media.poster === undefined ? {} : { poster: media.poster })}
      aria-label={media.alt}
      preload="none"
      muted
      loop
      playsInline
      controls
    />
  ) : (
    <img style={frame} src={media.src} alt={media.alt} loading="lazy" />
  );
}

/**
 * Chips first, typing only if allowed.
 *
 * Choosing is not the same work as composing. Asked to write what gets in
 * their way, most people give up or answer "laziness"; offered five specific
 * options, they pick the true one — and the true one is what the weekly
 * review needs to be worth anything.
 */
function InputBlock({
  input,
  text,
  onText,
  onPick,
}: {
  input: Input;
  text: string;
  onText: (value: string) => void;
  onPick: (value: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
      {input.chips && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {input.chips.map((chip) => (
            <button
              key={chipValue(chip)}
              type="button"
              onClick={() => onPick(chipValue(chip))}
              style={chipButton}
            >
              {chipLabel(chip)}
            </button>
          ))}
        </div>
      )}
      {input.allowFreeText === true && (
        <input
          value={text}
          onChange={(event) => onText(event.target.value)}
          {...(input.placeholder === undefined ? {} : { placeholder: input.placeholder })}
          style={textField}
        />
      )}
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  display: 'grid',
  placeItems: 'center',
  padding: 16,
  background: 'var(--coach-scrim, rgba(15, 18, 22, 0.62))',
  zIndex: SCRIM_Z,
};

const bubble: React.CSSProperties = {
  position: 'fixed',
  zIndex: BUBBLE_Z,
  padding: 16,
  borderRadius: 14,
  background: 'var(--coach-surface, var(--aj-surface, #fff))',
  color: 'var(--coach-ink, var(--aj-ink, #16181d))',
  boxShadow: '0 12px 32px rgba(0,0,0,0.22)',
};

const titleStyle: React.CSSProperties = { display: 'block', marginBottom: 4, fontSize: 15 };
const bodyStyle: React.CSSProperties = { margin: '0 0 12px', fontSize: 14, lineHeight: 1.45 };
const row: React.CSSProperties = { display: 'flex', gap: 8, justifyContent: 'flex-end' };

const baseButton: React.CSSProperties = {
  border: 'none',
  borderRadius: 999,
  padding: '8px 14px',
  fontSize: 14,
  cursor: 'pointer',
  minHeight: 40,
};

const primaryButton: React.CSSProperties = {
  ...baseButton,
  background: 'var(--coach-ring, var(--aj-accent, #C2410C))',
  color: 'var(--coach-on-accent, #fff)',
};

const ghostButton: React.CSSProperties = {
  ...baseButton,
  background: 'transparent',
  color: 'var(--coach-muted, var(--aj-subtle, #6b7280))',
};

const chipButton: React.CSSProperties = {
  ...baseButton,
  padding: '6px 12px',
  background: 'var(--coach-sunken, rgba(0,0,0,0.06))',
  color: 'inherit',
};

const textField: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 15,
  borderRadius: 10,
  border: '1px solid var(--coach-line, rgba(0,0,0,0.16))',
  background: 'transparent',
  color: 'inherit',
};
